import type { BoardElementType, WallCard, WallShape } from '@walls/store';
import { isClusterCard } from '../selectors/boardCardSelectors';
import type { BoardRect } from '../selection/utils/selectionGeometry';
import { isImageCard } from '../cards/utils/cardDisplay';
import { getCardFrameDimensions } from '../cards/utils/cardGeometry';
import { getShapeType } from '../drawing/utils/shapeAttributes';
import { getShapeBounds } from '../shapes/geometry/shapeGeometry';
import type { BoardElementRect } from './types';

export const defaultClusterWidth = 220;
export const defaultCollapsedClusterHeight = 80;
const defaultEmptyClusterBodyHeight = 48;
const defaultTextCardHeight = 80;
export const clusterRenderedTitleHeight = 58;
export const clusterChildGap = 8;
export const clusterChildInset = 10;

export function getCardRect(card: WallCard, offset = { x: 0, y: 0 }): BoardRect | null {
  if (card.xPos == null || card.yPos == null) {
    return null;
  }

  const dimensions = getPositionedCardDimensions(card);

  return {
    height: dimensions.height,
    width: dimensions.width,
    x: offset.x + card.xPos,
    y: offset.y + card.yPos,
  };
}

export function getPositionedCardDimensions(card: WallCard): { height: number; width: number } {
  if (isClusterCard(card)) {
    if (card.collapsed) {
      return {
        height: defaultCollapsedClusterHeight,
        width: defaultClusterWidth,
      };
    }

    return {
      height: card.height ?? getAutoClusterHeight(card),
      width: card.width ?? getAutoClusterWidth(card),
    };
  }

  const dimensions = getCardFrameDimensions(card);

  return {
    height: dimensions.height ?? (isImageCard(card) ? 150 : defaultTextCardHeight),
    width: dimensions.width,
  };
}

function getAutoClusterHeight(card: WallCard): number {
  const childCards = card.cards ?? [];

  if (childCards.length === 0) {
    return clusterRenderedTitleHeight + clusterChildInset * 2 + defaultEmptyClusterBodyHeight;
  }

  const childHeights = childCards.reduce<number>(
    (totalHeight, childCard) => totalHeight + getPositionedCardDimensions(childCard).height,
    0
  );
  const childGaps = Math.max(childCards.length - 1, 0) * clusterChildGap;

  return clusterRenderedTitleHeight + clusterChildInset * 2 + childHeights + childGaps;
}

function getAutoClusterWidth(card: WallCard): number {
  const childCards = card.cards ?? [];

  if (childCards.length === 0) {
    return defaultClusterWidth;
  }

  const childWidth = childCards.reduce(
    (width, childCard) => Math.max(width, getPositionedCardDimensions(childCard).width),
    0
  );

  return Math.max(defaultClusterWidth, childWidth + clusterChildInset * 2);
}

export function getBoardCardElements(cards: WallCard[]): BoardElementRect[] {
  return getCardElements(cards);
}

export function getBoardCardRectMap(cards: WallCard[]) {
  return new Map(getBoardCardElements(cards).map((element) => [element.id, element.rect]));
}

export function getSelectableBoardElements(
  cards: WallCard[],
  shapes: WallShape[]
): BoardElementRect[] {
  return [...getCardElements(cards), ...getShapeElements(shapes)];
}

export function getClusterChildElements(parentCluster: WallCard): BoardElementRect[] {
  return getClusterChildLayout(parentCluster).flatMap(({ card, rect }) => [
    buildCardElement(card, rect),
    ...(!isClusterCard(card) || card.collapsed
      ? []
      : getCardElements(card.cards ?? [], { x: rect.x, y: rect.y }, card)),
  ]);
}

function getCardElements(
  cards: WallCard[],
  offset = { x: 0, y: 0 },
  parentCluster?: WallCard
): BoardElementRect[] {
  if (parentCluster && !parentCluster.collapsed) {
    return getClusterChildLayout(parentCluster, offset).flatMap(({ card, rect }) => [
      buildCardElement(card, rect),
      ...(!isClusterCard(card) || card.collapsed
        ? []
        : getCardElements(card.cards ?? [], { x: rect.x, y: rect.y }, card)),
    ]);
  }

  return cards.flatMap((card) => {
    const rect = getCardRect(card, offset);
    const type: BoardElementType = isClusterCard(card) ? 'cluster' : 'card';
    const ownElement = rect
      ? [
          {
            card,
            id: card.id,
            isCollapsed: card.collapsed,
            isLocked: card.isLocked,
            isResized: card.width != null || card.height != null,
            parentId: card.parentId,
            rect,
            type,
            zPos: card.zPos ?? 0,
          },
        ]
      : [];

    if (!isClusterCard(card) || card.collapsed) {
      return ownElement;
    }

    const childOffset = rect ? { x: rect.x, y: rect.y } : offset;

    return [...ownElement, ...getCardElements(card.cards ?? [], childOffset, card)];
  });
}

export function getClusterChildLayout(parentCluster: WallCard, offset = { x: 0, y: 0 }) {
  const orderedChildren = [...(parentCluster.cards ?? [])].sort(
    (a, b) => (a.zPos ?? 0) - (b.zPos ?? 0)
  );
  const autoClusterChildWidth =
    parentCluster.width == null && parentCluster.height == null
      ? getAutoClusterWidth(parentCluster) - clusterChildInset * 2
      : null;
  let nextY = offset.y + clusterRenderedTitleHeight + clusterChildInset;

  return orderedChildren.flatMap((card) => {
    const dimensions = getPositionedCardDimensions(card);
    const x =
      offset.x + (parentCluster.width == null ? clusterChildInset : card.xPos ?? clusterChildInset);
    const y =
      parentCluster.height == null && parentCluster.width == null
        ? nextY
        : card.yPos == null
        ? nextY
        : offset.y + card.yPos;
    const rect = {
      height: dimensions.height,
      width:
        autoClusterChildWidth == null
          ? dimensions.width
          : Math.max(dimensions.width, autoClusterChildWidth),
      x,
      y,
    };

    nextY = y + dimensions.height + clusterChildGap;

    return [{ card, rect }];
  });
}

function buildCardElement(card: WallCard, rect: BoardRect): BoardElementRect {
  return {
    card,
    id: card.id,
    isCollapsed: card.collapsed,
    isLocked: card.isLocked,
    isResized: card.width != null || card.height != null,
    parentId: card.parentId,
    rect,
    type: isClusterCard(card) ? ('cluster' as const) : ('card' as const),
    zPos: card.zPos ?? 0,
  };
}

function getShapeElements(shapes: WallShape[]): BoardElementRect[] {
  return shapes.flatMap((shape) => {
    const shapeType = getShapeType(shape);

    return [
      {
        id: shape.id,
        isLocked: shape.isLocked,
        rect: getShapeBounds(shape),
        shape,
        type:
          shapeType === 'line'
            ? ('line' as const)
            : shapeType === 'text'
            ? ('text' as const)
            : ('shape' as const),
        zPos: shape.zPos ?? 0,
      },
    ];
  });
}
